import React from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { AccountRole } from "@common/types/domains/accounting/account";
import {
    AccrualLineAccountMode,
    getAccrualLineAllowedAccountTypes,
} from "@common/helpers/accrualLineAccountTypes";
import {
    BaseCellProps,
    CalculatedAmountCell,
    CellType,
    NumberCell,
} from "@components/spreadsheet/cells";
import { CellValue, ColumnDef } from "@components/spreadsheet/types";
import { TransactionSplitClassCell } from "@components/transactions/TransactionSplitV2/TransactionSplitClassCell";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    NativeInvoiceAdjustmentMode,
    NativeInvoiceLineRow,
} from "./nativeInvoiceFormTypes";
import { CatalogItemSearchCell } from "./CatalogItemSearchCell";
import { CatalogItemSelection } from "./CatalogItemCombobox";
import { NativeInvoiceAccrualAccountCell } from "./NativeInvoiceAccrualAccountCell";
import { SuggestedLineCell } from "./SuggestedLineCell";
import styles from "./SidePanel.module.scss";

interface BuildLineItemColumnsParams {
    entityId?: number;
    currency?: string;
    // Rows in grid order, used only to look up whether a given row is a
    // billable-expense-derived line (quantity/rate must be read-only for
    // those — see isEditable below). Not otherwise consumed by this builder.
    lines: NativeInvoiceLineRow[];
    onSelectCatalogItem: (
        lineId: string,
        selection: CatalogItemSelection,
    ) => void;
    showClassColumn: boolean;
    showReceivablesAccountColumn: boolean;
    showTaxColumn: boolean;
    showDiscountColumn: boolean;
    showDepositColumn: boolean;
    taxMode: NativeInvoiceAdjustmentMode;
    discountMode: NativeInvoiceAdjustmentMode;
    depositMode: NativeInvoiceAdjustmentMode;
    suggestedAccountLineIds?: readonly string[];
    onConfirmAccountSuggestion?: (lineId: string) => void;
    suggestedItemLineIds?: readonly string[];
    onConfirmItemSuggestion?: (lineId: string) => void;
    accrualMode?: AccrualLineAccountMode;
    isAccountReviewHighlighted?: boolean;
}

// A billable-expense-derived line's amount must always match the linked
// transaction's expense amount (see
// NativeInvoiceBillableExpenseInternalService#assertLinesMatchTransactions),
// so quantity/rate are locked for that row only — every other cell in the row
// (item name, description, coding, tax/discount/deposit) stays editable.
function isQuantityOrRateEditable(
    lines: NativeInvoiceLineRow[],
    cell: { row: number },
): boolean {
    return lines[cell.row]?.billableExpenseTransactionId == null;
}

export const BILLABLE_EXPENSE_LOCKED_TOOLTIP =
    "This line item is linked to a billable expense. Quantity and rate cannot be edited.";

// Wraps a locked Quantity/Rate NumberCell with the billable-expense lock
// explanation; editable rows fall through to the default NumberCell render
// (returning undefined) so the tooltip only ever appears over cells the
// user can't act on.
function billableExpenseLockedNumberCellFn(
    lines: NativeInvoiceLineRow[],
    isMonetary: boolean,
    props: BaseCellProps,
) {
    if (isQuantityOrRateEditable(lines, { row: props.rowIdx })) {
        return undefined;
    }
    return (
        <Tooltip content={BILLABLE_EXPENSE_LOCKED_TOOLTIP} side="top">
            <span className={styles.trigger}>
                <NumberCell
                    {...props}
                    value={typeof props.value === "number" ? props.value : null}
                    isMonetary={isMonetary}
                />
            </span>
        </Tooltip>
    );
}

// Zero/empty numeric cells read as placeholders (tertiary) until the user
// fills in a real value.
function emptyNumberCellClassName(field: string) {
    return (row: Record<string, CellValue>): string => {
        const value = row[field];
        return typeof value === "number" && value !== 0
            ? ""
            : styles.emptyNumberCell;
    };
}

// Builds the ordered set of spreadsheet grid columns for the invoice
// line-items table. Class and Receivables are appended conditionally so their
// columns only render when those capabilities/entity setup allow it (see
// LineItemsSection). Income account is always present.
export function buildLineItemColumns({
    entityId,
    currency,
    lines,
    onSelectCatalogItem,
    showClassColumn,
    showReceivablesAccountColumn,
    showTaxColumn,
    showDiscountColumn,
    showDepositColumn,
    taxMode,
    discountMode,
    depositMode,
    suggestedAccountLineIds = [],
    onConfirmAccountSuggestion,
    suggestedItemLineIds = [],
    onConfirmItemSuggestion,
    accrualMode,
    isAccountReviewHighlighted,
}: BuildLineItemColumnsParams): ColumnDef[] {
    const isBill = accrualMode === "bills";
    const lineAccountTypes = accrualMode
        ? getAccrualLineAllowedAccountTypes(accrualMode)
        : undefined;
    const renderAccountCell = (
        props: BaseCellProps,
        defaultAccountRole: AccountRole,
        allowedAccountTypes?: AccountType[],
    ) => {
        const lineId = typeof props.row.id === "string" ? props.row.id : null;
        const onConfirmSuggestion =
            lineId !== null &&
            onConfirmAccountSuggestion &&
            suggestedAccountLineIds.includes(lineId)
                ? () => onConfirmAccountSuggestion(lineId)
                : undefined;
        return (
            <NativeInvoiceAccrualAccountCell
                {...props}
                value={typeof props.value === "string" ? props.value : null}
                entityId={entityId}
                allowedAccountTypes={allowedAccountTypes}
                defaultAccountRole={defaultAccountRole}
                onConfirmSuggestion={onConfirmSuggestion}
            />
        );
    };
    return [
        {
            field: "productOrService",
            label: "Item",
            cellType: CellType.STRING,
            width: 150,
            ...(!isBill
                ? {
                      isEditOnSingleClick: true,
                      cellFn: (props: BaseCellProps) => {
                          const lineId =
                              typeof props.row.id === "string"
                                  ? props.row.id
                                  : null;
                          const itemCell = (
                              <CatalogItemSearchCell
                                  {...props}
                                  entityId={entityId}
                                  currency={currency}
                                  onSelectCatalogItem={onSelectCatalogItem}
                              />
                          );
                          if (
                              lineId === null ||
                              !onConfirmItemSuggestion ||
                              !suggestedItemLineIds.includes(lineId)
                          ) {
                              return itemCell;
                          }
                          return (
                              <SuggestedLineCell
                                  onConfirm={() =>
                                      onConfirmItemSuggestion(lineId)
                                  }
                              >
                                  {itemCell}
                              </SuggestedLineCell>
                          );
                      },
                  }
                : {}),
        },
        {
            field: "description",
            label: "Description",
            cellType: CellType.STRING,
            width: 140,
        },
        {
            field: "quantity",
            label: "Quantity",
            cellType: CellType.NUMBER,
            allowNegative: false,
            width: 80,
            headerClassName: styles.numericHeader,
            isEditable: (cell) => isQuantityOrRateEditable(lines, cell),
            className: emptyNumberCellClassName("quantity"),
            cellFn: (props) =>
                billableExpenseLockedNumberCellFn(lines, false, props),
        },
        {
            field: "unitAmount",
            label: "Rate",
            cellType: CellType.NUMBER,
            isMonetary: true,
            width: 104,
            headerClassName: styles.numericHeader,
            isEditable: (cell) => isQuantityOrRateEditable(lines, cell),
            className: emptyNumberCellClassName("unitAmount"),
            cellFn: (props) =>
                billableExpenseLockedNumberCellFn(lines, true, props),
        },
        {
            field: "amount",
            label: "Amount",
            cellType: CellType.NUMBER,
            isMonetary: true,
            width: 112,
            headerClassName: styles.numericHeader,
            isEditable: false,
            className: styles.amountCell,
            cellFn: (props) => <CalculatedAmountCell {...props} />,
        },
        ...(showTaxColumn
            ? [
                  {
                      field: "tax",
                      label: "Tax",
                      cellType: CellType.NUMBER,
                      isMonetary: taxMode === "amount",
                      width: 88,
                      className: emptyNumberCellClassName("tax"),
                  } satisfies ColumnDef,
              ]
            : []),
        ...(showDiscountColumn
            ? [
                  {
                      field: "discount",
                      label: "Discount",
                      cellType: CellType.NUMBER,
                      isMonetary: discountMode === "amount",
                      width: 88,
                      className: emptyNumberCellClassName("discount"),
                  } satisfies ColumnDef,
              ]
            : []),
        ...(showDepositColumn
            ? [
                  {
                      field: "deposit",
                      label: "Deposit",
                      cellType: CellType.NUMBER,
                      isMonetary: depositMode === "amount",
                      width: 88,
                      className: emptyNumberCellClassName("deposit"),
                  } satisfies ColumnDef,
              ]
            : []),
        ...(showClassColumn
            ? [
                  {
                      field: "classIds",
                      label: "Class",
                      // Portaled ClassCombobox; default CellType.CLASS mounts
                      // ClassFilterOverlay in-cell and gets clipped here.
                      cellType: CellType.CLASS,
                      width: 120,
                      cellFn: (props) => {
                          const classValue =
                              Array.isArray(props.value) || props.value === null
                                  ? props.value
                                  : null;
                          return (
                              <TransactionSplitClassCell
                                  {...props}
                                  value={classValue}
                              />
                          );
                      },
                  } satisfies ColumnDef,
              ]
            : []),
        {
            field: "accrualAccountId",
            label: isBill ? "Expense account" : "Income account",
            cellType: CellType.STRING,
            width: 160,
            className: isAccountReviewHighlighted
                ? styles.reviewTaskHighlight
                : undefined,
            cellFn: (props) =>
                renderAccountCell(
                    props,
                    isBill
                        ? AccountRole.uncategorizedExpense
                        : AccountRole.defaultRevenue,
                    lineAccountTypes,
                ),
        },
        ...(showReceivablesAccountColumn
            ? [
                  {
                      field: "receivablesAccountId",
                      // Match the invoices by-line-item view label.
                      label: isBill
                          ? "Payables account"
                          : "Receivables account",
                      cellType: CellType.STRING,
                      width: 160,
                      className: isAccountReviewHighlighted
                          ? styles.reviewTaskHighlight
                          : undefined,
                      cellFn: (props) =>
                          renderAccountCell(
                              props,
                              isBill
                                  ? AccountRole.accountsPayable
                                  : AccountRole.accountsReceivable,
                              [
                                  isBill
                                      ? AccountType.accountsPayable
                                      : AccountType.accountsReceivable,
                              ],
                          ),
                  } satisfies ColumnDef,
              ]
            : []),
    ];
}
